import { useEffect, useState } from "react";
import { Send, Download, Trash2, Heart } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Panel, Empty, Stat, inputCls } from "@/components/kit";
import { api, errMsg, fmtDay } from "@/lib/api";
import { FormDialog, SectionHead } from "./common";

function exportCsv(fans, team) {
  const rows = [["Name", "Email", "Joined"], ...fans.map((f) => [f.name, f.email, f.created_at.slice(0, 10)])];
  const csv = rows.map((r) => r.map((c) => `"${String(c || "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `${team.slug}-fans.csv`;
  a.click();
}

export default function Fans({ teamId, team }) {
  const [fans, setFans] = useState([]);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState({ subject: "", message: "" });

  const load = () => api.get(`/teams/${teamId}/fans`).then((r) => setFans(r.data));
  useEffect(() => {
    load();
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    try {
      const { data } = await api.post(`/teams/${teamId}/fans/broadcast`, msg);
      toast.success(`Email queued for ${data.queued} fans`);
      setOpen(false);
      setMsg({ subject: "", message: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (f) => {
    if (!window.confirm(`Remove ${f.email}?`)) return;
    await api.delete(`/teams/${teamId}/fans/${f.id}`);
    load();
  };

  return (
    <div className="space-y-6">
      <SectionHead title="Fans" text="Supporters who follow your hub by email.">
        <Button variant="outline" className="rounded-full border-white/15 bg-transparent" disabled={!fans.length} onClick={() => exportCsv(fans, team)} data-testid="export-fans-btn"><Download className="mr-1 h-4 w-4" /> Export CSV</Button>
        <Button className="rounded-full font-bold" disabled={!fans.length} onClick={() => setOpen(true)} data-testid="broadcast-fans-btn"><Send className="mr-1 h-4 w-4" /> Email fans</Button>
      </SectionHead>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3"><Stat label="Followers" value={fans.length} testid="fans-count" /></div>
      <Panel>
        {fans.length ? (
          <div className="divide-y divide-white/5">
            {fans.map((f) => (
              <div key={f.id} className="flex items-center justify-between gap-3 py-3" data-testid={`fan-row-${f.email}`}>
                <div><p className="font-semibold text-white">{f.name || "Fan"}</p><p className="text-xs text-slate-500">{f.email} · since {fmtDay(f.created_at)}</p></div>
                <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => del(f)} data-testid={`delete-fan-${f.email}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        ) : <Empty icon={Heart} title="No fans yet" text="Share your hub link — fans can follow from the hub page." />}
      </Panel>
      <FormDialog open={open} onOpenChange={setOpen} title="Email all fans" onSubmit={send} submitLabel="Send" testid="broadcast-form">
        <Field label="Subject"><Input required value={msg.subject} onChange={(e) => setMsg({ ...msg, subject: e.target.value })} data-testid="broadcast-subject-input" /></Field>
        <Field label="Message"><textarea required className={`${inputCls} h-32`} value={msg.message} onChange={(e) => setMsg({ ...msg, message: e.target.value })} data-testid="broadcast-message-input" /></Field>
      </FormDialog>
    </div>
  );
}
